import React, { useEffect, useState } from 'react';
import {
  useGetAdminConfigQuery, useSetAdminConfigMutation,
  useGetEmployeeUsageNotifyQuery, useSetEmployeeUsageNotifyMutation,
  useGetSharedMailboxesQuery, useSetSharedMailboxesMutation,
  useLazyGetDocusignStatusQuery, useLazyGetDocusignConsentUrlQuery,
  useLazyGetDocusignUserinfoQuery, useLazyDocusignWarmupQuery,
  useLazyListDocusignTemplatesQuery, useLazyInspectDocusignTemplateQuery,
  useGetSlackStatusQuery,
} from '../app/api';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
  amber:     '#b7791f',
};

// The admin config checklist, grouped into sections. Each item `key` matches a
// backend flag name. Add new toggles by extending a section's items (and the
// backend ADMIN_CONFIG_DEFAULTS registry).
const CONFIG_SECTIONS = [
  {
    title: 'Sign-in page',
    description: 'Control which external sign-in links appear on the main hub login page.',
    items: [
      {
        key: 'show_client_portal_link',
        label: 'Show Client portal link',
        help: 'When off, the Client Portal link is hidden on the main hub sign-in page.',
      },
      {
        key: 'show_entrepreneur_portal_link',
        label: 'Show Entrepreneur portal link',
        help: 'When off, the Entrepreneur sign-in link is hidden on the main hub sign-in page.',
      },
    ],
  },
  {
    title: 'Hub feedback',
    description: 'The floating “Hub Feedback” button in the bottom-right corner of every hub page. Submissions land in Super Admin → Feedback.',
    items: [
      {
        key: 'show_feedback_button',
        label: 'Show the Hub Feedback button',
        help: 'When off, the button disappears for everyone and no new feedback can be sent. Feedback already submitted is kept and stays visible on the Feedback page.',
      },
    ],
  },
  {
    title: 'Client setup',
    description: 'Options for the Work-side new-client workflow.',
    items: [
      {
        key: 'show_hubspot_client_import',
        label: 'Show “Create new client from HubSpot”',
        help: 'When off, the HubSpot search is hidden in Work → Clients and clients are added manually only.',
      },
      {
        key: 'show_test_household_option',
        label: 'Show “This is a test household” option',
        help: 'When off, the beta-testing checkbox is hidden in Work → Clients - every new household is treated as real and its documents go to the production (WORM-locked) bucket. Turn off once beta testing wraps up to prevent new test households from being created by mistake.',
      },
    ],
  },
  {
    title: 'Client documents',
    description: 'Options for browsing client-uploaded documents (Work and the B2B firm portal).',
    items: [
      {
        key: 'show_download_buttons',
        label: 'Show document Download buttons',
        help: 'When off, only “View” (the in-app viewer) is available — the Download button is hidden everywhere client documents are browsed.',
      },
    ],
  },
  {
    title: 'Employee links',
    description: 'External links surfaced to all employees in the hub.',
    items: [
      {
        key: 'it_support_url',
        type: 'text',
        label: 'IT Support link',
        help: 'URL for the “IT Support” button on the Career Opportunities page (opens in a new tab). Must start with http:// or https://. Leave blank to hide the button.',
      },
    ],
  },
  {
    title: 'Meetings Assistant (beta)',
    description: 'The Meetings page shows the signed-in employee\u2019s own calendar. Choosing a meeting and its participants builds an AI prep brief from their past email with those people, including any Zocks or Zoom meeting recaps. Visible only to people assigned the Beta role (Admin Panel \u2192 Roles). The generated brief is stored; the emails behind it never are.',
    items: [
      {
        key: 'enable_meetings',
        label: 'Enable the Meetings Assistant',
        help: 'When off, the page is hidden and the endpoints refuse everyone \u2014 use this as the kill switch. Requires the same delegated Mail.Read and Calendars.Read consent as Your Day.',
      },
      {
        key: 'meetings_lookback_days',
        type: 'number',
        label: 'History lookback (days)',
        help: 'How far back to search email with a participant. 730 (two years) is the default, chosen so annual-review meetings still find the previous conversation.',
      },
      {
        key: 'meetings_max_messages_per_person',
        type: 'number',
        label: 'Messages per participant (max)',
        help: 'Ceiling on how much mail one brief reads per person. Caps both AI cost and how much of a mailbox is sent for analysis. 60 is the default.',
      },
      {
        key: 'meetings_rate_limit_per_hour',
        type: 'number',
        label: 'Briefs per person per hour',
        help: 'Stops a refresh loop running up an AI bill. Going over returns \u201ctry again shortly\u201d. 20 is the default.',
      },
      {
        key: 'meetings_recap_senders',
        label: 'Meeting-recap senders',
        help: 'Comma-separated fragments matched against the sender address, e.g. \u201czocks, zoom.us, otter.ai\u201d. Mail from these is treated as a record of a previous meeting and weighted above ordinary email. Leave blank to use the built-in list.',
      },
    ],
  },
  {
    title: 'Manager Role Alignment reviews',
    description: 'The “My Team → Role Alignment” page, which shows a manager how far their team has '
      + 'got with the Role Alignment Questionnaire, lets them read a direct report’s answers, and asks '
      + 'them for a Managerial Assessment of each one. Off until you open the review window. Employees '
      + 'can still fill in their own questionnaire the whole time, and Tech → Forms → Role Alignment '
      + 'still shows every submission — this switch only controls what MANAGERS can see and are asked for.',
    items: [
      {
        key: 'enable_team_role_alignment',
        label: 'Open manager reviews',
        help: 'Off by default. While it is off the page is hidden from managers, the endpoint behind it '
          + 'refuses to hand over anyone’s answers, and no assessment can be filed — so a bookmarked URL '
          + 'is not a way round it. Managers with assessments outstanding also stop being badged in the '
          + 'sidebar. Turn it on when the review period starts; turning it back off hides everything again '
          + 'without deleting a single submitted form.',
      },
    ],
  },
  {
    title: 'Ask about Role Alignment forms',
    description: 'Adds a question box to Tech \u2192 Forms \u2192 Role Alignment, above the results. '
      + 'A question is answered by AI reading every submitted questionnaire and its manager\u2019s '
      + 'assessment \u2014 the same forms that page already shows. Only people with the Tech role can '
      + 'reach it. These are candid self-assessments, so every question asked is recorded (who, what, '
      + 'when); the answer and the form contents are not stored.',
    items: [
      {
        key: 'enable_forms_qa',
        label: 'Enable the question box',
        help: 'Off by default. When off the box is hidden and the endpoint refuses everyone \u2014 use this as the kill switch. '
          + 'Questions are recorded whether this is on or off; the switch stops the feature, not the record.',
      },
      {
        key: 'forms_qa_rate_limit_per_hour',
        type: 'number',
        label: 'Questions per person per hour',
        help: 'Each question re-sends every submitted form to the AI, so this is the cost control as much as the abuse control. '
          + 'At 160 employees a question costs roughly $0.35. 10 is the default.',
      },
    ],
  },
  {
    title: 'Filing mail from Your Day (beta)',
    description: 'Adds a “File to…” picker to each message on the Your Day briefing, so an assistant can move a message straight into a folder (a priority folder, say) without leaving the hub. This is the only part of the hub that can change a mailbox, so it is off until you turn it on — and it needs delegated Mail.ReadWrite consent on the Entra app registration, which Microsoft offers no narrower alternative to for moving mail. Each move is recorded with who moved it, when, and the destination folder name; subjects and message contents are never stored. Deleted Items and Junk are deliberately not offered as targets.',
    items: [
      {
        key: 'enable_mail_move',
        label: 'Enable filing mail into folders',
        help: 'When off, the picker is hidden and the endpoint refuses everyone — the kill switch. Employees also need the Beta role, and each person consents to write access the first time they file a message (reading the briefing never asks for it).',
      },
      {
        key: 'mail_move_limit_per_hour',
        type: 'number',
        label: 'Moves per person per hour',
        help: 'Filing a morning’s mail is a burst, so this is deliberately generous; it exists to stop a stuck retry loop rearranging a mailbox. Going over returns “try again shortly”. 60 is the default.',
      },
    ],
  },
  {
    title: 'Clearing Bloom items from Your Day (beta)',
    description: 'Adds a small X to each rock and a checkbox to each to-do on the Your Day Bloom tile, so someone can clear finished work without opening Bloom. Both ask for confirmation first. Read what each one does, because Bloom does not treat them the same: archiving a rock removes it from every L10 board that carried it, for the whole team, and can only be undone in Bloom itself (Rocks → Archived → Restore). Bloom offers no archive for to-dos at all, so the checkbox marks the to-do genuinely COMPLETE — the meeting sees it as done and it counts toward that L10’s to-do completion percentage. Bloom has only one service account, so Bloom’s own history will attribute every change to it; the hub records who really clicked, on which item, and when. Off until you turn it on.',
    items: [
      {
        key: 'enable_bloom_actions',
        label: 'Enable archiving rocks and completing to-dos',
        help: 'When off, the buttons are hidden and the endpoints refuse everyone — the kill switch. Employees also need the Beta role. Each change is checked against the Bloom account we matched them to, so nobody can clear an item that isn’t theirs.',
      },
      {
        key: 'bloom_action_limit_per_hour',
        type: 'number',
        label: 'Changes per person per hour',
        help: 'Applies to rock archives and to-do completions separately, so this number is the cap on each. Deliberately tighter than mail filing: the point is to stop a stuck retry loop emptying the firm’s EOS board. Going over returns “try again shortly”. 20 is the default.',
      },
    ],
  },
  {
    title: 'Tasks (beta)',
    description: 'The Tasks page is a hub view of Microsoft To Do AND Planner in one place, mimicking the unified Planner app: a rail of lists and plans on the left, tasks on the right. It is the only one of these pages that WRITES to Microsoft \u2014 ticking a task off, and creating one. Nothing is copied into the hub, so every change lands in Microsoft immediately and there is no sync to drift. Visible only to people assigned the Beta role (Admin Panel \u2192 Roles). Requires delegated Tasks.ReadWrite and User.ReadBasic.All consent on the Entra app registration. NOTE ON ASSIGNING WORK: Microsoft To Do cannot accept a task from another person \u2014 there is no permission for it \u2014 so assigning a task to a colleague creates a PLANNER task in a plan you pick, which then appears in their To Do under \u201cAssigned to me\u201d. That also means an assigned task is visible to everyone in that plan; there is no private hand-off anywhere in Microsoft 365. Planner Premium plans are a different Microsoft product and never appear here.',
    items: [
      {
        key: 'enable_tasks',
        label: 'Enable the Tasks page',
        help: 'When off, the page is hidden and the endpoints refuse everyone \u2014 use this as the kill switch. Worth knowing that this switch gates writes as well as reads, unlike the other beta pages.',
      },
      {
        key: 'tasks_max_per_list',
        label: 'Maximum tasks shown per list',
        help: 'To Do keeps completed tasks forever, so a list someone has used for years can be thousands of rows. This caps what one list or plan loads. 200 is the default.',
        type: 'number',
      },
    ],
  },
  {
    title: 'Planner summary (beta)',
    description: 'The Planner page finds the Microsoft Planner boards an employee is involved in — ones they own through a Team, ones shared with them directly, and ones they were simply assigned tasks in — counts what each is carrying, and has AI write a short read on where each project stands. Read-only: nothing is written back to Planner and no task data is stored. Visible only to people assigned the Beta role (Admin Panel → Roles). Planner Premium plans are a different Microsoft product and never appear here. The three switches below control where boards are found; switching the last two off can make the page look empty, because most of our boards are not Team-owned.',
    items: [
      {
        key: 'enable_planner',
        label: 'Enable the Planner summary',
        help: 'When off, the page is hidden and the endpoint refuses everyone — use this as the kill switch. Requires delegated Tasks.Read consent on the Entra app registration.',
      },
      {
        key: 'enable_planner_ai',
        label: 'Enable the AI summary on that page',
        help: 'Off leaves the whole page working — counts, buckets and task lists — but spends no AI tokens. Use this if the figures are useful but the written summary is not worth the cost.',
      },
      {
        key: 'enable_planner_rosters',
        label: 'Include plans not attached to a Team',
        help: 'Planner boards come in two kinds: ones that live in a Team or group, and ones shared with people directly (what you get from “Create a plan” in the Planner app). Most of ours are the second kind, and Microsoft only lists those through a preview API — so leave this on unless that preview starts misbehaving, in which case turning it off falls back to Team-owned plans only.',
      },
      {
        key: 'enable_planner_assigned',
        label: 'Include boards you were assigned work in',
        help: 'Finds boards owned by someone else that you hold tasks in \u2014 Microsoft doesn\u2019t report those as \u201cyour\u201d plans, so without this they are invisible even though your work is on them. Where the rest of a board can\u2019t be read, the page shows only your own tasks and labels it as such.',
      },
      {
        key: 'planner_activity_days',
        type: 'number',
        label: 'Recent activity window (days)',
        help: 'What “recent” means on that page. IMPORTANT: Planner’s API reports only when a task was created and when it was completed — it publishes no “last modified” date — so a wider window shows a longer stretch of those two events and still cannot show edits, reassignments or due-date changes. 7 is the default.',
      },
      {
        key: 'planner_max_plans',
        type: 'number',
        label: 'Plans read per request (max)',
        help: 'Each plan costs two Microsoft calls, against a service that throttles hard and publishes no limits, so someone who belongs to 40 Teams needs a ceiling. The page says plainly when it read fewer plans than the person belongs to. 12 is the default.',
      },
      {
        key: 'planner_max_tasks_per_plan',
        type: 'number',
        label: 'Tasks read per plan (max)',
        help: 'Ceiling on one plan’s task list. A plan that hits it is flagged on the page as a floor rather than a total. 400 is the default.',
      },
      {
        key: 'planner_rate_limit_per_hour',
        type: 'number',
        label: 'Summaries per person per hour',
        help: 'Stops a refresh loop running up an AI bill and getting the firm throttled by Planner. Going over returns “try again shortly”. 20 is the default.',
      },
    ],
  },
  {
    title: 'Your Day (AI briefings)',
    description: 'The Your Day page reads the signed-in employee’s own Outlook inbox and calendar and uses AI to tier this morning’s mail and summarize the past week. Visible only to people assigned the Beta role (Admin Panel → Roles). Turning the first switch off hides the page immediately for everyone, whatever their roles.',
    items: [
      {
        key: 'enable_your_day',
        label: 'Enable Your Day briefings',
        help: 'When off, the page returns “not found” for everyone — use this as the kill switch. Requires delegated Mail.Read and Calendars.Read consent on the Entra app registration to actually work.',
      },
      {
        key: 'enable_your_day_filed_mail',
        label: 'Include mail filed into Inbox subfolders',
        help: 'Once someone files a briefing message into a folder, that message is no longer in the Inbox \u2014 so without this it silently drops off the next briefing, which reads as the hub having lost it. On leaves it visible, tagged with the folder it now lives in. Note that filed mail shares the messages-per-briefing ceiling below rather than adding to it, so raise that if a busy folder starts crowding out new mail.',
      },
      {
        key: 'your_day_max_messages',
        type: 'number',
        label: 'Messages per briefing (max)',
        help: 'Ceiling on how many messages one briefing reads. Caps both the AI cost per briefing and how much of a mailbox is sent for analysis. 50 is the default.',
      },
      {
        key: 'your_day_rate_limit_per_hour',
        type: 'number',
        label: 'Briefings per person per hour',
        help: 'The page generates a briefing when it loads, so this is what stops a refresh loop from running up an AI bill. Going over returns “try again in a while”.',
      },
      {
        key: 'your_day_timezone',
        type: 'text',
        inputType: 'text',
        placeholder: 'America/New_York',
        label: 'Business-day time zone',
        help: 'IANA zone used for the “since 5:00 PM the previous business day” cutoff (Monday reaches back to Friday). Leave blank for America/New_York.',
      },
      {
        key: 'your_day_chunk_size',
        type: 'number',
        label: 'Messages per parallel batch',
        help: 'A busy inbox is tiered in concurrent batches of this size, because the wait is driven by how much the AI writes, not how much it reads. Lower = more parallelism and a shorter wait; 15 is the default. Inboxes at or under this size use a single faster call.',
      },
      {
        key: 'your_day_model_id',
        type: 'text',
        inputType: 'text',
        placeholder: 'us.anthropic.claude-haiku-4-5',
        label: 'Model for tiering (advanced)',
        help: 'Blank uses the firm default (Sonnet). Sorting mail into tiers is a classification job, so a faster model here is the biggest single speed-up available — try us.anthropic.claude-haiku-4-5. The model must be enabled in Bedrock first, and check the briefing quality before leaving it set.',
      },
      {
        key: 'enable_week_review',
        label: 'Enable Week in Review tile',
        help: 'The recall summary of the past week, shown under the calendar. This is the expensive half — it reads several times as much mail as one morning briefing — so it can be switched off on its own without losing the page.',
      },
      {
        key: 'week_review_days',
        type: 'number',
        label: 'Week in Review lookback (days)',
        help: 'How far back the review reads. 7 matches a weekly team meeting.',
      },
      {
        key: 'week_review_max_messages',
        type: 'number',
        label: 'Week in Review messages (max)',
        help: 'Ceiling on messages read per review. A week of mail is much larger than a morning’s, so this is the main cost control. 150 is the default.',
      },
      {
        key: 'week_review_rate_limit_per_hour',
        type: 'number',
        label: 'Week reviews per person per hour',
        help: 'Lower than the briefing limit on purpose — it’s a weekly artifact and each run is dearer. The page also caches it for an hour, so a normal session generates it once.',
      },
      {
        key: 'enable_day_extras',
        label: 'Enable the counted tiles',
        help: 'Waiting on a Reply, the calendar-shape strip, and Coming Up (birthdays and work anniversaries). These are computed, not AI-generated — no model call and no extra Bedrock cost. They do add two Outlook reads per briefing, which run alongside the existing ones.',
      },
      {
        key: 'awaiting_reply_days',
        type: 'number',
        label: 'Waiting on a Reply — lookback (days)',
        help: 'How far back to look for mail you sent that never got an answer. 14 is the default. Longer windows cost another Outlook page, not another model call.',
      },
      {
        key: 'awaiting_reply_min_age_days',
        type: 'number',
        label: 'Waiting on a Reply — minimum age (days)',
        help: 'Threads quieter than this are not shown — mail sent this morning isn’t overdue. Following up restarts the clock, so a thread you chased yesterday drops off until it goes quiet again. 2 is the default.',
      },
      {
        key: 'enable_bloom_tile',
        label: 'Enable Bloom rocks & to-dos tile',
        help: 'Reads each person’s open Bloom Growth rocks and to-dos live. Requires an IT-managed Bloom service account (BLOOM_USERNAME / BLOOM_PASSWORD in the environment) — Bloom has no API keys, only a password grant, so one service account avoids storing any employee’s Bloom password. People appear once they’re an attendee on a meeting the service account can see. The tile hides itself if Bloom is unconfigured or unreachable; the briefing is unaffected either way.',
      },
      {
        key: 'briefing_muted_domains',
        label: 'Muted sender domains',
        help: 'Comma-separated domains whose mail is kept out of the briefing entirely — tool notifications rather than correspondence, e.g. bloomgrowth.com. Filtered before the AI sees it, so muted mail costs no tokens as well as no attention. The page still reports how many were muted; it never hides mail silently. Leave blank to mute nothing.',
      },
      {
        key: 'enable_shared_mailboxes',
        label: 'Allow shared & delegated mailboxes',
        help: 'Puts a mailbox menu on the left of Your Day / Your Week, so people can switch the briefing to a shared mailbox (service@, operations@) or to a colleague’s mailbox they have been given access to. ON. Use this as the kill switch: off, the menu disappears and the API refuses any mailbox but the caller’s own, exactly as before the feature existed. Needs delegated Mail.Read.Shared and Calendars.Read.Shared consent on the Entra app registration — and Mail.ReadWrite.Shared as well if people are to file mail in those mailboxes; until that consent is granted the menu just shows an “Allow other mailboxes” button and nothing breaks. Nobody gains access to a mailbox from this switch: Outlook decides, and it is asked again on every single read.',
      },
      {
        key: 'enable_mailbox_discovery',
        label: 'Find each person’s mailboxes automatically',
        help: 'Fills every employee’s menu on its own, so nobody has to know or type an address. Microsoft offers no way to ask “which mailboxes can I open”, so this works by trying every mailbox in the tenant with that person’s own sign-in and keeping the ones Outlook opens — about 290 here, batched into roughly 22 requests, run in the background once a week per person and cached. It reads no mail: the check only asks whether the Inbox can be opened. Turning it off falls back to the published list below plus anything people add by hand, which still works but means somebody maintains a list.',
      },
      {
        key: 'enable_own_mailbox_adds',
        label: 'Let employees add a mailbox by address',
        help: 'A fallback for anything the automatic scan misses — a mailbox outside the directory, or one granted between scans. The address is checked against Outlook before it is saved, and again on every read, so somebody adding a mailbox they cannot open is told so rather than getting access. Off restricts everyone to the scan plus the published list. Has no effect while the first switch is off.',
      },
      {
        key: 'mailbox_scan_ttl_hours',
        type: 'number',
        label: 'Re-check mailbox access every (hours)',
        help: 'How long an automatic scan is trusted before the page quietly runs another. 168 (a week) is the default: delegate access changes rarely and each scan is ~22 background requests. People also get a “Rescan mailboxes” link for the day they are granted a mailbox and want it now.',
      },
      {
        key: 'people_dates_days_ahead',
        type: 'number',
        label: 'Coming Up — days ahead',
        help: 'How far ahead to look for birthdays and work anniversaries. A window rather than just today, because the directory is sparse here — only 14 of 160 people currently have either date filled in, so a same-day tile would almost always be empty.',
      },
    ],
  },
  {
    title: 'Slack in the briefings',
    description: 'Adds each person’s Slack — direct messages, @-mentions, and replies in threads they were part of — to their At A Glance pages. The messages are sorted into the SAME three tiers as their email, so “what needs me today” stays one answer, and on Your Week they are folded into the themes alongside the mail and meetings. A Slack tab shows those same messages again on their own, grouped by conversation the way Slack is — for when the question is “what has happened in Slack” rather than “what needs me”. That tab is a view of the same read, so it costs no extra Slack calls and can never disagree with the priorities about what arrived. Read-only: the hub never posts, marks anything read, or joins a channel. Channel traffic that does not mention the person is deliberately not read — that is what people open Slack for. Each employee connects their own account from the At A Glance page and can disconnect it there at any time; nobody is connected by an admin, and nobody sees anybody else’s Slack.',
    items: [
      {
        key: 'enable_slack_summaries',
        label: 'Enable Slack in Your Day / Your Week',
        help: 'Off by default, and worth a deliberate decision rather than an inherited one. This is the only feature in the hub that STORES a long-lived credential: Slack offers no other way, because bot tokens can read neither direct messages nor search, so each person grants a personal read-only token through Slack’s own consent screen. Those tokens are encrypted in the database, are only ever used for the request of the person they belong to, and are revoked with Slack when somebody disconnects. Compliance approved this specific trade on 31 Aug 2026. Needs a Slack app registered in the workspace and SLACK_CLIENT_ID / SLACK_CLIENT_SECRET set in the environment — see docs/slack-summaries.md; until then the connect button says Slack is not set up. Turning this off hides the connect button, refuses new connections and leaves briefings email-only; existing connections survive and resume if it is switched back on, and disconnecting stays available to everyone regardless.',
      },
      {
        key: 'enable_slack_tab',
        label: 'Show a separate Slack tab',
        help: 'OFF. The same Slack messages are already sorted into the tiers on Your Day and folded into the themes on Your Week, so a third view of them is redundant for most people and costs a tab’s worth of attention. Turning it on adds a Slack tab that groups those same messages by conversation the way Slack does, with the tier kept as a coloured dot — useful for browsing rather than triaging. It is a view of the briefing, not a second fetch, so it costs no extra Slack calls either way. Only ever visible to someone who has connected an account, and never while a shared mailbox is selected.',
      },
      {
        key: 'slack_max_messages',
        type: 'number',
        label: 'Slack messages per briefing (max)',
        help: 'Ceiling on how many Slack messages one morning briefing reads. Separate from the email ceiling on purpose: they share one AI call, so a single shared budget would let a loud morning in Slack push email out of the briefing. 40 is the default.',
      },
      {
        key: 'slack_week_max_messages',
        type: 'number',
        label: 'Slack messages per Week in Review (max)',
        help: 'The same ceiling for the seven-day review, which reads far more of everything. 120 is the default.',
      },
    ],
  },
  {
    title: 'Ask a question (At A Glance)',
    description: 'A box at the top of At A Glance where an employee types a question — “what’s outstanding with the Brennans?” — and gets a short answer drawn from their own email, calendar and Slack, with the specific messages and meetings it used listed underneath and clickable. Unlike the briefing, this searches their WHOLE mailbox and Slack history rather than a window, using Outlook’s and Slack’s own search indexes, so it can reach a thread from two years ago. It reads message previews, not full email bodies. Read-only: it cannot reply, post, or change a calendar. Every question is recorded — who asked, what they asked, when, which mailbox, and how many results each source returned — and the ANSWER is deliberately not stored, so the hub never keeps a second copy of client correspondence.',
    items: [
      {
        key: 'enable_glance_ask',
        label: 'Enable the question box',
        help: 'On by default. Unlike the briefings this spends nothing until somebody actually types a question, and it reads the same mailbox they are already having summarized. Off hides the box and the endpoint refuses everyone — use this as the kill switch. Questions are recorded whether it is on or off; the switch stops the feature, not the record. Asking a question of a shared mailbox works and is scoped exactly like the briefing; Slack is only ever searched for the person’s own account.',
      },
      {
        key: 'ask_rate_limit_per_hour',
        type: 'number',
        label: 'Questions per person per hour',
        help: 'Generous next to the 6 briefings per hour, because a question is typed deliberately rather than fired off by a page load — this bounds AI cost, not a refresh loop. Going over returns “try again in a while”. 30 is the default, on its own budget so a run of questions never stops someone’s morning briefing from rebuilding.',
      },
      {
        key: 'ask_max_per_source',
        type: 'number',
        label: 'Results per source (max)',
        help: 'The ceiling that actually drives cost: email, calendar and Slack each contribute up to this many results, and the total is what one answer reads. 40 is the default. Raising it finds more but costs proportionally more per question and slows the answer.',
      },
    ],
  },
  {
    title: 'Calendar (shared Outlook calendars)',
    description: 'Shows every calendar already in an employee’s Outlook on one grid, and lets them create, edit and delete meetings. This is the only feature that WRITES to Microsoft — saving or deleting a meeting emails every attendee, including clients. Requires delegated Calendars.ReadWrite and Calendars.ReadWrite.Shared consent on the Entra app registration. Adding MailboxSettings.Read is optional — without it, meetings tagged with a RENAMED or custom Outlook category fall back to their calendar’s colour; the six built-in categories work either way.',
    items: [
      {
        key: 'enable_calendar',
        label: 'Enable Calendar page',
        help: 'Off hides the page and returns “not found” for everyone. Use this as the kill switch if a change goes wrong — it takes effect immediately, without a redeploy.',
      },
      {
        key: 'calendar_write_limit_per_hour',
        type: 'number',
        label: 'Calendar changes per person per hour',
        help: 'Caps creates, edits and deletes only. Viewing calendars is never throttled. This exists to bound an accidental loop, not to limit normal use — 60 is the default.',
      },
      {
        key: 'calendar_default_view',
        type: 'text',
        inputType: 'text',
        placeholder: 'week',
        label: 'Default view',
        help: 'Which view the page opens on: week or day. Mobile always uses day.',
      },
      {
        key: 'calendar_legacy_domains',
        type: 'text',
        inputType: 'text',
        placeholder: 'oneteamfinancial.com, otherfirm.com',
        label: 'Former firm email domains',
        help: 'Comma separated, bare domains — no @ and no https://. Meetings organized by one of these came across during the tenant merge and still belong to an account that no longer exists here, so nobody can edit or cancel them. The Calendar page flags them and shows a count, which is how you size the cleanup and confirm it worked once the domains move over. Leave blank to switch the flagging off.',
      },
    ],
  },
  {
    title: 'Hub Dashboard',
    description: 'Lookahead windows for the upcoming birthdays and work-anniversary cards.',
    items: [
      {
        key: 'birthday_lookahead_days',
        type: 'number',
        label: 'Birthday lookahead (days)',
        help: 'How many days out an upcoming birthday must fall to appear on the Hub dashboard.',
      },
      {
        key: 'anniversary_lookahead_days',
        type: 'number',
        label: 'Anniversary lookahead (days)',
        help: 'How many days out an upcoming work anniversary must fall to appear on the Hub dashboard.',
      },
    ],
  },
];

export default function SuperAdminConfig() {
  const { data, isLoading, refetch } = useGetAdminConfigQuery();
  const [setConfig] = useSetAdminConfigMutation();

  // Local mirror of the flags so the checkboxes respond instantly; seeded from
  // the server and re-synced whenever the query data changes.
  const [flags, setFlags] = useState({});
  const [numberDrafts, setNumberDrafts] = useState({});
  const [textDrafts, setTextDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState(null);

  /* Which sections are open. All shut on arrival.
   *
   * This page grew one card at a time until it was a couple of thousand pixels
   * of switches, and the cost of that is not scrolling — it is that nobody can
   * see what the page CONTAINS. Collapsed, the whole thing is a list of
   * features, each with a one-line count of what is on inside it, and opening
   * one is a decision rather than a hunt.
   *
   * Deliberately not persisted. A remembered set of open cards means the page
   * looks different for reasons nobody remembers causing, and reopening one
   * card is cheap.
   */
  const [openSections, setOpenSections] = useState({});
  const toggleSection = (title) =>
    setOpenSections((s) => ({ ...s, [title]: !s[title] }));
  const allOpen = CONFIG_SECTIONS.every((s) => openSections[s.title]);
  const setAllSections = (open) =>
    setOpenSections(open
      ? Object.fromEntries(CONFIG_SECTIONS.map((s) => [s.title, true]))
      : {});

  /* Slack's own state, read here and nowhere else on this page.
   *
   * The one setting whose switch can be ON and still do nothing: it needs a
   * Slack app registered for the environment, which is not something a switch
   * can conjure. Without this the admin turns it on, sees no change anywhere,
   * and has no way to find out why — the employee-facing page deliberately
   * shows nothing at all in that state, because an employee cannot act on it.
   */
  const { data: slackStatus } = useGetSlackStatusQuery();

  useEffect(() => {
    if (data?.config) {
      setFlags(data.config);
      setNumberDrafts(data.config);
      setTextDrafts(data.config);
    }
  }, [data]);

  const onToggle = async (key) => {
    const next = !flags[key];
    setFlags((f) => ({ ...f, [key]: next }));
    setSavingKey(key);
    setError(null);
    try {
      const res = await setConfig({ name: key, value: next }).unwrap();
      if (res?.config) setFlags(res.config);
    } catch (e) {
      // Roll the toggle back and surface the failure.
      setFlags((f) => ({ ...f, [key]: !next }));
      setError(e?.data?.detail || 'Could not save that setting. Please try again.');
      refetch();
    } finally {
      setSavingKey(null);
    }
  };

  const onNumberBlur = async (key) => {
    const n = parseInt(numberDrafts[key], 10);
    if (!Number.isFinite(n) || n < 1) {
      setNumberDrafts((d) => ({ ...d, [key]: flags[key] }));
      return;
    }
    if (n === flags[key]) {
      setNumberDrafts((d) => ({ ...d, [key]: n }));
      return;
    }
    setSavingKey(key);
    setError(null);
    try {
      const res = await setConfig({ name: key, value: n }).unwrap();
      if (res?.config) {
        setFlags(res.config);
        setNumberDrafts(res.config);
      }
    } catch (e) {
      setNumberDrafts((d) => ({ ...d, [key]: flags[key] }));
      setError(e?.data?.detail || 'Could not save that setting. Please try again.');
      refetch();
    } finally {
      setSavingKey(null);
    }
  };

  const onTextBlur = async (key) => {
    const val = (textDrafts[key] ?? '').trim();
    const current = (flags[key] ?? '').toString().trim();
    if (val === current) {
      setTextDrafts((d) => ({ ...d, [key]: val }));
      return;
    }
    setSavingKey(key);
    setError(null);
    try {
      const res = await setConfig({ name: key, value: val }).unwrap();
      if (res?.config) {
        setFlags(res.config);
        setTextDrafts(res.config);
      }
    } catch (e) {
      setTextDrafts((d) => ({ ...d, [key]: flags[key] ?? '' }));
      setError(e?.data?.detail || 'Could not save that setting. Please try again.');
      refetch();
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: '1.5rem' }}>
      <div style={{ background: brand.deepBlue, borderRadius: '12px', padding: '1.75rem 2rem', marginBottom: '1.5rem', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: '300px', height: '100%', background: 'linear-gradient(135deg, transparent 40%, rgba(0,105,166,0.3))', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '3px', background: brand.goldenrod }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: 'rgba(208,155,44,0.15)', border: `2px solid ${brand.goldenrod}`, borderRadius: '12px', padding: '0.75rem', flexShrink: 0 }}>
            <i className="bi bi-sliders" style={{ color: brand.goldenrod, fontSize: '1.75rem' }} />
          </div>
          <div>
            <p style={{ color: brand.goldenrod, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 4px' }}>
              Super Admin
            </p>
            <h3 style={{ color: 'white', fontWeight: 800, margin: '0 0 4px' }}>Config</h3>
            <p style={{ color: 'rgba(255,255,255,0.65)', margin: 0, fontSize: '0.875rem' }}>
              Application-wide settings. Changes apply immediately.
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ maxWidth: '760px', marginBottom: '1rem', color: brand.red, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <i className="bi bi-exclamation-circle" /> {error}
        </div>
      )}

      {!isLoading && (
        <div style={{ maxWidth: '760px', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ color: brand.charcoal, fontSize: '0.8rem' }}>
            {CONFIG_SECTIONS.length} areas
          </span>
          <button
            type="button"
            onClick={() => setAllSections(!allOpen)}
            style={{
              marginLeft: 'auto', background: 'none', border: 'none', padding: 0,
              color: brand.tealBlue, fontWeight: 700, fontSize: '0.82rem',
              textDecoration: 'underline', cursor: 'pointer',
            }}
          >
            {allOpen ? 'Collapse all' : 'Expand all'}
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-4"><div className="spinner-border text-primary" role="status" /></div>
      ) : (
        CONFIG_SECTIONS.map((section) => {
          const open = Boolean(openSections[section.title]);
          // The one-line summary a collapsed card has to earn its place with:
          // how many settings are in here, and how many of the switches are on.
          // Numbers and text boxes are counted in the total but not in the
          // "on" figure — there is no on for a message-count ceiling.
          const switches = section.items.filter((it) => !it.type);
          const switchesOn = switches.filter((it) => Boolean(flags[it.key])).length;
          const summary = [
            `${section.items.length} setting${section.items.length === 1 ? '' : 's'}`,
            switches.length ? `${switchesOn} of ${switches.length} on` : null,
          ].filter(Boolean).join(' · ');
          return (
          <div key={section.title} className="card" style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px', maxWidth: '760px', marginBottom: '1rem' }}>
            <button
              type="button"
              onClick={() => toggleSection(section.title)}
              aria-expanded={open}
              style={{
                width: '100%', textAlign: 'left', background: 'white',
                border: 'none', borderRadius: '12px',
                padding: open ? '1.25rem 1.5rem 0.5rem' : '1.25rem 1.5rem',
                display: 'flex', alignItems: 'center', gap: '1rem', cursor: 'pointer',
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <h5 style={{ color: brand.deepBlue, fontWeight: 800, margin: 0 }}>{section.title}</h5>
                {/* Collapsed, the summary stands in for the description. Open,
                    the full description is right below and repeating a count
                    over it would just be noise. */}
                {!open && (
                  <div style={{ color: brand.charcoal, fontSize: '0.8rem', marginTop: '0.15rem' }}>
                    {summary}
                  </div>
                )}
              </div>
              <i
                className={`bi bi-chevron-${open ? 'up' : 'down'}`}
                style={{ color: brand.charcoal, flexShrink: 0 }}
              />
            </button>
            {open && (
            <div className="card-body" style={{ padding: '0 1.5rem 1.5rem' }}>
              <p style={{ color: brand.charcoal, fontSize: '0.85rem', margin: '0 0 1rem' }}>
                {section.description}
              </p>
              {/* A switch that is on and still does nothing is the worst state
                  this page can be in, so it is called out where the switch is
                  rather than left for somebody to work out from silence. */}
              {section.title === 'Slack in the briefings'
                && slackStatus?.enabled && slackStatus?.configured === false && (
                <div style={{
                  background: 'rgba(183,121,31,0.1)', border: `1px solid ${brand.amber}`,
                  borderRadius: 8, padding: '0.6rem 0.8rem', marginBottom: '1rem',
                  color: brand.deepBlue, fontSize: '0.82rem',
                }}>
                  <i className="bi bi-exclamation-triangle-fill" style={{ color: brand.amber }} />
                  {' '}<strong>Switched on, but this environment has no Slack app yet.</strong>
                  {' '}Nobody can connect and no briefing will include Slack until
                  SLACK_CLIENT_ID and SLACK_CLIENT_SECRET are set — see
                  docs/slack-summaries.md for the app registration and scopes.
                </div>
              )}
              {section.items.map((item, i) => (
                item.type === 'text' ? (
                  <div
                    key={item.key}
                    style={{ padding: '0.85rem 0.5rem', borderTop: i === 0 ? 'none' : '1px solid #eef2f6' }}
                  >
                    <label htmlFor={`cfg-${item.key}`} style={{ display: 'block' }}>
                      <span style={{ fontWeight: 700, color: brand.deepBlue }}>{item.label}</span>
                      {savingKey === item.key && (
                        <span style={{ color: brand.charcoal, fontSize: '0.75rem', marginLeft: '0.5rem' }}>
                          <i className="bi bi-arrow-repeat" /> saving…
                        </span>
                      )}
                      <div style={{ color: brand.charcoal, fontSize: '0.8rem', margin: '0.15rem 0 0.5rem' }}>{item.help}</div>
                    </label>
                    <input
                      // Defaults to a URL field for backwards compatibility (the
                      // only text setting used to be a link); set `inputType` for
                      // free-text values like a time zone, which a url input rejects.
                      type={item.inputType || 'url'}
                      id={`cfg-${item.key}`}
                      value={textDrafts[item.key] ?? ''}
                      placeholder={item.placeholder || 'https://support.example.com'}
                      onChange={(e) => setTextDrafts((d) => ({ ...d, [item.key]: e.target.value }))}
                      onBlur={() => onTextBlur(item.key)}
                      disabled={savingKey === item.key}
                      style={{ width: '100%', border: '1px solid #cdd5dd', borderRadius: 6, padding: '0.4rem 0.6rem', fontSize: '0.9rem', color: brand.deepBlue }}
                    />
                  </div>
                ) : item.type === 'number' ? (
                  <div
                    key={item.key}
                    style={{ padding: '0.85rem 0.5rem', borderTop: i === 0 ? 'none' : '1px solid #eef2f6', display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}
                  >
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <span style={{ fontWeight: 700, color: brand.deepBlue }}>{item.label}</span>
                      {savingKey === item.key && (
                        <span style={{ color: brand.charcoal, fontSize: '0.75rem', marginLeft: '0.5rem' }}>
                          <i className="bi bi-arrow-repeat" /> saving…
                        </span>
                      )}
                      <div style={{ color: brand.charcoal, fontSize: '0.8rem', marginTop: '0.15rem' }}>{item.help}</div>
                    </div>
                    <input
                      type="number"
                      min={1}
                      id={`cfg-${item.key}`}
                      value={numberDrafts[item.key] ?? ''}
                      onChange={(e) => setNumberDrafts((d) => ({ ...d, [item.key]: e.target.value }))}
                      onBlur={() => onNumberBlur(item.key)}
                      disabled={savingKey === item.key}
                      style={{ width: 90, border: '1px solid #cdd5dd', borderRadius: 6, padding: '0.35rem 0.5rem', fontSize: '0.9rem', color: brand.deepBlue }}
                    />
                  </div>
                ) : (
                  <div
                    key={item.key}
                    className="form-check"
                    style={{ padding: '0.85rem 0.5rem 0.85rem 2rem', borderTop: i === 0 ? 'none' : '1px solid #eef2f6' }}
                  >
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`cfg-${item.key}`}
                      checked={Boolean(flags[item.key])}
                      onChange={() => onToggle(item.key)}
                      disabled={savingKey === item.key}
                      style={{ cursor: 'pointer' }}
                    />
                    <label className="form-check-label" htmlFor={`cfg-${item.key}`} style={{ cursor: 'pointer' }}>
                      <span style={{ fontWeight: 700, color: brand.deepBlue }}>{item.label}</span>
                      {savingKey === item.key && (
                        <span style={{ color: brand.charcoal, fontSize: '0.75rem', marginLeft: '0.5rem' }}>
                          <i className="bi bi-arrow-repeat" /> saving…
                        </span>
                      )}
                      <div style={{ color: brand.charcoal, fontSize: '0.8rem', marginTop: '0.15rem' }}>{item.help}</div>
                    </label>
                  </div>
                )
              ))}
            </div>
            )}
          </div>
          );
        })
      )}

      <SharedMailboxes />

      <EmployeeUsageNotify />

      <DocusignTools />
    </div>
  );
}

/* The shared mailboxes offered in everyone's Your Day mailbox menu.
 *
 * One per line, `address` or `address, Label`. Its own card rather than a config
 * flag because it is a list, and ADMIN-gated on read as well as write — unlike
 * the switches above, which the login page reads before anyone has signed in.
 *
 * THE IMPORTANT THING ABOUT THIS BOX, stated on the card itself because the
 * natural assumption is the opposite: publishing a mailbox here grants nobody
 * anything. Each person's menu is filtered by asking Outlook whether they can
 * actually open it, so an address listed here is invisible to everyone who
 * hasn't been delegated it in Exchange.
 */
function SharedMailboxes() {
  const { data, isLoading } = useGetSharedMailboxesQuery();
  const [save, { isLoading: saving }] = useSetSharedMailboxesMutation();
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.mailboxes) {
      setValue(data.mailboxes
        .map((m) => (m.label ? `${m.address}, ${m.label}` : m.address))
        .join('\n'));
    }
  }, [data]);

  const onSave = async () => {
    setError(null); setSaved(false);
    // Line-per-mailbox with an optional label after a comma. Labels can contain
    // spaces, so the split is on the FIRST comma only.
    const mailboxes = value.split(/[\n;]+/).map((line) => {
      const [address, ...rest] = line.split(',');
      return { address: (address || '').trim(), label: rest.join(',').trim() };
    }).filter((m) => m.address);
    try {
      const res = await save({ mailboxes }).unwrap();
      setValue((res?.mailboxes || [])
        .map((m) => (m.label ? `${m.address}, ${m.label}` : m.address))
        .join('\n'));
      setSaved(true);
    } catch (e) {
      setError(e?.data?.detail || 'Could not save the mailbox list. Please try again.');
    }
  };

  return (
    <div className="card" style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px', maxWidth: '760px', marginBottom: '1rem' }}>
      <div className="card-body" style={{ padding: '1.5rem' }}>
        <h5 style={{ color: brand.deepBlue, fontWeight: 800, margin: '0 0 0.25rem' }}>Your Day shared mailboxes</h5>
        <p style={{ color: brand.charcoal, fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
          <strong>Optional.</strong> With “Find each person’s mailboxes automatically” on, every
          shared and delegated mailbox someone can open already appears in their menu without
          being listed here. Use this only to pin a mailbox that should be visible before
          anyone’s first scan, or one the directory doesn’t cover. One per line, as{' '}
          <code>address</code> or <code>address, Label</code>.
        </p>
        <p style={{ color: brand.charcoal, fontSize: '0.85rem', margin: '0 0 1rem' }}>
          <strong>Listing a mailbox here grants nobody access to it.</strong> Each person only
          sees the ones Outlook already lets them open — we ask Microsoft, every time — so it is
          safe to publish the full set and let Exchange sort out who sees what.
        </p>
        {isLoading ? (
          <div className="spinner-border spinner-border-sm text-primary" role="status" />
        ) : (
          <>
            <textarea
              value={value}
              onChange={(e) => { setValue(e.target.value); setSaved(false); }}
              rows={4}
              placeholder={'service@prosperitycapitaladvisors.com, Client Service\ninfo@prosperitycapitaladvisors.com'}
              style={{ width: '100%', border: '1px solid #cdd5dd', borderRadius: 8, padding: '0.5rem 0.65rem', fontSize: '0.9rem', color: brand.deepBlue, fontFamily: 'monospace' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.6rem' }}>
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                style={{ border: `1px solid ${brand.deepBlue}`, background: brand.deepBlue, color: 'white', borderRadius: 8, padding: '0.45rem 0.9rem', fontSize: '0.85rem', fontWeight: 700, cursor: saving ? 'wait' : 'pointer' }}
              >
                {saving ? 'Saving…' : 'Save mailboxes'}
              </button>
              {saved && <span style={{ color: brand.green, fontSize: '0.85rem' }}><i className="bi bi-check-circle-fill me-1" />Saved</span>}
            </div>
            {error && (
              <div style={{ marginTop: '0.6rem', color: brand.red, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <i className="bi bi-exclamation-circle" /> {error}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// Recipients emailed when a hub employee clicks "Request more usage" on My Team
// / My Usage. Comma- or line-separated list, stored via the admin config API.
function EmployeeUsageNotify() {
  const { data, isLoading } = useGetEmployeeUsageNotifyQuery();
  const [save, { isLoading: saving }] = useSetEmployeeUsageNotifyMutation();
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.emails) setValue(data.emails.join(', '));
  }, [data]);

  const onSave = async () => {
    setError(null); setSaved(false);
    const emails = value.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    try {
      const res = await save({ emails }).unwrap();
      setValue((res?.emails || []).join(', '));
      setSaved(true);
    } catch (e) {
      setError(e?.data?.detail || 'Could not save recipients. Please try again.');
    }
  };

  return (
    <div className="card" style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px', maxWidth: '760px', marginBottom: '1rem' }}>
      <div className="card-body" style={{ padding: '1.5rem' }}>
        <h5 style={{ color: brand.deepBlue, fontWeight: 800, margin: '0 0 0.25rem' }}>Employee Claude usage requests</h5>
        <p style={{ color: brand.charcoal, fontSize: '0.85rem', margin: '0 0 1rem' }}>
          Who gets emailed when a hub employee clicks “Request more usage” on their My Team / My Usage page. Comma- or line-separated. Leave blank to disable the notification.
        </p>
        {isLoading ? (
          <div className="spinner-border spinner-border-sm text-primary" role="status" />
        ) : (
          <>
            <textarea
              value={value}
              onChange={(e) => { setValue(e.target.value); setSaved(false); }}
              rows={3}
              placeholder="it@prosperitycapitaladvisors.com, admin@prosperitycapitaladvisors.com"
              style={{ width: '100%', border: '1px solid #cdd5dd', borderRadius: 8, padding: '0.5rem 0.65rem', fontSize: '0.9rem', color: brand.deepBlue }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.6rem' }}>
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                style={{ border: `1px solid ${brand.deepBlue}`, background: brand.deepBlue, color: 'white', borderRadius: 8, padding: '0.45rem 0.9rem', fontSize: '0.85rem', fontWeight: 700, cursor: saving ? 'wait' : 'pointer' }}
              >
                {saving ? 'Saving…' : 'Save recipients'}
              </button>
              {saved && <span style={{ color: brand.green, fontSize: '0.85rem' }}><i className="bi bi-check-circle-fill me-1" />Saved</span>}
            </div>
            {error && (
              <div style={{ marginTop: '0.6rem', color: brand.red, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <i className="bi bi-exclamation-circle" /> {error}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// DocuSign setup + go-live helpers. Runs the admin-gated /docusign/* endpoints
// with the session token attached (so no more "no auth" from calling them
// directly), and shows each result inline.
function DocusignTools() {
  const [runStatus] = useLazyGetDocusignStatusQuery();
  const [runConsent] = useLazyGetDocusignConsentUrlQuery();
  const [runUserinfo] = useLazyGetDocusignUserinfoQuery();
  const [runWarmup] = useLazyDocusignWarmupQuery();
  const [runTemplates] = useLazyListDocusignTemplatesQuery();
  const [runInspect] = useLazyInspectDocusignTemplateQuery();

  const [busy, setBusy] = useState(null);
  const [result, setResult] = useState(null); // { label, data }
  const [error, setError] = useState(null);

  const run = async (label, trigger, arg) => {
    setBusy(label); setError(null);
    try {
      const data = await trigger(arg).unwrap();
      setResult({ label, data });
    } catch (e) {
      setError(e?.data?.detail || e?.error || 'Request failed.');
      setResult(null);
    } finally {
      setBusy(null);
    }
  };

  const actions = [
    { label: 'Warm up (go-live calls)', primary: true, go: () => run('Warm up (go-live calls)', runWarmup, { calls: 20 }) },
    { label: 'Status', go: () => run('Status', runStatus) },
    { label: 'Consent URL', go: () => run('Consent URL', runConsent) },
    { label: 'User info (Account ID)', go: () => run('User info (Account ID)', runUserinfo) },
    { label: 'List templates', go: () => run('List templates', runTemplates) },
    { label: 'Inspect template', go: () => run('Inspect template', runInspect, { templateId: 'default' }) },
  ];

  return (
    <div className="card" style={{ border: 'none', boxShadow: '0 2px 12px rgba(10,41,71,0.1)', borderRadius: '12px', maxWidth: '760px', marginBottom: '1rem' }}>
      <div className="card-body" style={{ padding: '1.5rem' }}>
        <h5 style={{ color: brand.deepBlue, fontWeight: 800, margin: '0 0 0.25rem' }}>DocuSign</h5>
        <p style={{ color: brand.charcoal, fontSize: '0.85rem', margin: '0 0 1rem' }}>
          Setup &amp; go-live helpers. These run as you (admin), so no separate DocuSign login is needed.
          Run <strong>Warm up</strong> a couple of times to clear the ~20 API calls go-live requires, then submit the app for go-live in the DocuSign console.
        </p>

        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {actions.map(a => (
            <button
              key={a.label}
              type="button"
              onClick={a.go}
              disabled={!!busy}
              style={{
                border: `1px solid ${a.primary ? brand.deepBlue : '#cdd5dd'}`,
                background: a.primary ? brand.deepBlue : 'white',
                color: a.primary ? 'white' : brand.deepBlue,
                borderRadius: 8, padding: '0.45rem 0.8rem', fontSize: '0.85rem', fontWeight: 700,
                cursor: busy ? 'wait' : 'pointer', opacity: busy && busy !== a.label ? 0.6 : 1,
              }}
            >
              {busy === a.label ? 'Running…' : a.label}
            </button>
          ))}
        </div>

        {error && (
          <div style={{ marginTop: '1rem', color: brand.red, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <i className="bi bi-exclamation-circle" /> {error}
          </div>
        )}

        {result && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{ fontWeight: 700, color: brand.deepBlue, fontSize: '0.8rem', marginBottom: '0.35rem' }}>{result.label}</div>
            {result.label === 'Consent URL' && result.data?.consent_url && (
              <a href={result.data.consent_url} target="_blank" rel="noopener noreferrer" style={{ color: brand.tealBlue, fontWeight: 700, fontSize: '0.85rem', display: 'inline-block', marginBottom: '0.5rem' }}>
                <i className="bi bi-box-arrow-up-right me-1" /> Open consent URL to approve
              </a>
            )}
            <pre style={{ background: '#f4f6f9', border: '1px solid #e2e7ee', borderRadius: 8, padding: '0.75rem', fontSize: '0.75rem', color: brand.charcoal, maxHeight: 320, overflow: 'auto', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {JSON.stringify(result.data, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}
